// Match rows (one line per match, newest first) and a paginated list of them.

import type { MatchListResponse, MatchSummary } from '../src/api/contract';
import { describeError, matchUrl } from './api';
import { emptyState, errorState, h, loadingState, show, timeEl } from './dom';
import { endReasonText, endedText, fmtInt, fmtRelative, matchResult, plural, sliceText } from './format';
import { labLogo, modelLogo } from './lab-logos';
import { describeProgress, progressOf, stageBar } from './stagebar';

/** The result label: "Defender won", "Attacker won", "Draw", "Live", "Not counted". */
export function resultBadge(m: Pick<MatchSummary, 'status' | 'winner' | 'endReason'>): HTMLElement {
  const r = matchResult(m);
  return h('span', { class: `result result--${r.kind}` }, r.kind === 'live' ? h('span', { class: 'live-dot', attrs: { 'aria-hidden': 'true' } }) : null, r.label);
}

/** One match as a single clickable row. A running match links to the live page. */
export function matchRow(m: MatchSummary): HTMLElement {
  const running = m.status === 'running';
  const progress = progressOf(m);
  const aName = m.modelAName || m.modelA;
  const bName = m.modelBName || m.modelB;
  const when = m.completedAt ?? m.startedAt;
  const proven = m.stagesProven ?? progress.proven.size;
  const label = [
    `${matchResult(m).label}.`,
    `${aName} attacking ${bName}.`,
    running ? `In progress, turn ${fmtInt(m.totalTurns)}.` : `${endReasonText(m.endReason, m.setup)}, after ${plural(m.totalTurns, 'turn')}.`,
    describeProgress(progress),
    `${sliceText(m.setup, m.mode)}.`,
    fmtRelative(when) !== '—' ? `${running ? 'Started' : 'Ended'} ${fmtRelative(when)}.` : '',
  ].join(' ');
  return h(
    'a',
    { class: `mrow mrow--${matchResult(m).kind}`, attrs: { href: running ? '/live' : matchUrl(m.id), 'aria-label': label } },
    h('span', { class: 'mrow-result' }, resultBadge(m)),
    h(
      'span',
      { class: 'mrow-players' },
      h('span', { class: 'player' }, h('span', { class: 'player-name atk' }, (m.modelAMaker ? labLogo(m.modelAMaker, 14, { decorative: true }) : modelLogo(m.modelA, 14, { decorative: true })), aName), h('span', { class: 'player-role' }, 'attacker')),
      h('span', { class: 'mrow-vs', attrs: { 'aria-hidden': 'true' } }, 'vs'),
      h('span', { class: 'player' }, h('span', { class: 'player-name def' }, (m.modelBMaker ? labLogo(m.modelBMaker, 14, { decorative: true }) : modelLogo(m.modelB, 14, { decorative: true })), bName), h('span', { class: 'player-role' }, 'defender')),
    ),
    h('span', { class: 'mrow-stages' }, stageBar(progress, true, m.setup), h('span', { class: 'mrow-count' }, `${proven}/6`)),
    h(
      'span',
      { class: 'mrow-ended' },
      h('span', null, endedText(m)),
      h('span', { class: 'mrow-sub' }, running ? sliceText(m.setup, m.mode) : `${plural(m.totalTurns, 'turn')} · ${sliceText(m.setup, m.mode)}`),
    ),
    h('span', { class: 'mrow-when' }, timeEl(when)),
  );
}

/** Column labels over a list of rows (wide screens only; each row's own label says it all). */
function rowsHead(): HTMLElement {
  return h(
    'div',
    { class: 'mrows-head', attrs: { 'aria-hidden': 'true' } },
    h('span', null, 'Result'),
    h('span', null, 'Attacker vs defender'),
    h('span', null, 'Stages done'),
    h('span', null, 'How it ended'),
    h('span', { class: 'mrow-when' }, 'When'),
  );
}

/** A finished list of rows, for matches already in hand. */
export function matchRows(matches: MatchSummary[]): HTMLElement {
  return h('div', { class: 'mrows' }, rowsHead(), h('ul', null, ...matches.map((m) => h('li', null, matchRow(m)))));
}

export interface MatchListOptions {
  /** Loads a page; `before` is null for the newest page. */
  load: (before: string | null) => Promise<MatchListResponse>;
  /** Matches already in hand (e.g. from a profile response): shown without a request. */
  initial?: MatchSummary[];
  /** How many matches the endpoint behind `initial` returns at most: a full slice offers "Show older". */
  initialPageSize?: number;
  emptyTitle: string;
  emptyDetail?: string;
  /** Replaces "Show older matches" with a link (e.g. to the full match list). */
  moreLink?: { href: string; text: string };
}

/** Renders a match list with "Show older" paging into `container`. */
export async function matchList(container: HTMLElement, opts: MatchListOptions): Promise<void> {
  const list = h('ul');
  const box = h('div', { class: 'mrows' }, rowsHead(), list);
  const more = h('div', { class: 'more' });
  const seen = new Set<string>();
  let cursor: string | null = null;
  let started = false;

  const add = (matches: MatchSummary[]): void => {
    for (const m of matches) {
      if (!m || typeof m.id !== 'string' || seen.has(m.id)) continue;
      seen.add(m.id);
      list.append(h('li', null, matchRow(m)));
    }
  };

  const renderMore = (hasMore: boolean): void => {
    more.replaceChildren();
    if (opts.moreLink) {
      more.append(h('a', { class: 'btn', attrs: { href: opts.moreLink.href } }, opts.moreLink.text));
      return;
    }
    if (!hasMore) return;
    more.append(h('button', { class: 'btn', attrs: { type: 'button' }, on: { click: () => void loadPage() } }, 'Show older matches'));
  };

  const loadPage = async (): Promise<void> => {
    more.replaceChildren(loadingState('Loading more'));
    try {
      // Pages that only repeat matches already shown (or that a caller filtered empty) are skipped.
      for (let i = 0; i < 5; i++) {
        const res = await opts.load(started ? cursor : null);
        started = true;
        const before = seen.size;
        add(Array.isArray(res.matches) ? res.matches : []);
        cursor = typeof res.nextBefore === 'string' && res.nextBefore ? res.nextBefore : null;
        if (seen.size > before || cursor === null) break;
      }
      if (seen.size === 0) show(container, emptyState(opts.emptyTitle, opts.emptyDetail));
      else renderMore(cursor !== null);
    } catch (err) {
      more.replaceChildren(errorState(describeError(err), () => void loadPage()));
    }
  };

  if (opts.initial) {
    add(opts.initial);
    if (seen.size === 0) {
      show(container, emptyState(opts.emptyTitle, opts.emptyDetail));
      return;
    }
    show(container, box, more);
    // The initial slice has no cursor: "show more" starts from the newest page and skips duplicates.
    renderMore(opts.initial.length >= (opts.initialPageSize ?? 10));
    return;
  }

  show(container, loadingState('Loading matches'));
  try {
    const res = await opts.load(null);
    started = true;
    add(Array.isArray(res.matches) ? res.matches : []);
    cursor = typeof res.nextBefore === 'string' && res.nextBefore ? res.nextBefore : null;
  } catch (err) {
    show(container, errorState(describeError(err), () => void matchList(container, opts)));
    return;
  }
  if (seen.size === 0) {
    show(container, emptyState(opts.emptyTitle, opts.emptyDetail));
    return;
  }
  show(container, box, more);
  renderMore(cursor !== null);
}
